import { COLOR_GREY_200 } from '@eleveo/foundation';
import { CircleIcon, DashedLineUpIcon } from '@eleveo/icons-react';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CommonChartConfig, IntradaySupportedSeries, SeriesConfig } from './types';
import { RecordBooleanType } from '../../types';

export type IntradayChartSeriesLegendProps<T extends IntradaySupportedSeries> = {
  countOfVisibleSeries: number;
  seriesConfig: CommonChartConfig<T>['seriesConfig'];
  seriesDataVisibility: RecordBooleanType;
  seriesLegendEnabled: RecordBooleanType;
  seriesTrendLegendRelatedSeries: CommonChartConfig<T>['seriesTrendLegendRelatedSeries'];
  shouldRenderExtraRow?: boolean;
  toggleSeriesDataVisibility: (seriesName: string) => void;
};

export type IntradayChartSeriesLegendItemProps<T extends IntradaySupportedSeries> = {
  config: SeriesConfig<T>;
  isDataPresent: boolean;
  isDataVisible: boolean;
  isEnabled: boolean;
  toggleSeriesDataVisibility: () => void;
};

export const IntradayChartSeriesLegendItem = <T extends IntradaySupportedSeries>({
  config: { color, nameKey },
  isDataPresent,
  isDataVisible,
  isEnabled,
  toggleSeriesDataVisibility,
}: IntradayChartSeriesLegendItemProps<T>): React.ReactElement<IntradayChartSeriesLegendItemProps<T>> | null => {
  const { t } = useTranslation();

  if (!isDataPresent) {
    return null;
  }

  const name = nameKey as string;
  const isTrendSeries = name === 'trend';
  const Icon = isTrendSeries ? DashedLineUpIcon : CircleIcon;

  return (
    /* this is a temporary solution until new regular component will be prepared in UI kit */
    <button
      aria-pressed={isDataVisible}
      className={clsx('mbe-15 cursor-pointer hover:underline hover:text-blue-700', {
        'cursor-not-allowed': !isEnabled,
      })}
      data-test-id={`chart:legend-${name}-series:toggle`}
      disabled={!isEnabled}
      onClick={toggleSeriesDataVisibility}
      type="button"
    >
      <Icon
        className="mie-10"
        data-test-id={isTrendSeries ? 'dashed-line-up-icon' : 'circle-icon'}
        fontSize={16}
        color={isDataVisible ? color : COLOR_GREY_200}
      />
      {t(`intraday.charts.legendItems.${nameKey as string}`)}
    </button>
  );
};

export const IntradayChartSeriesLegend = <T extends IntradaySupportedSeries>({
  countOfVisibleSeries,
  seriesConfig,
  seriesDataVisibility,
  seriesLegendEnabled,
  seriesTrendLegendRelatedSeries,
  shouldRenderExtraRow,
  toggleSeriesDataVisibility,
}: IntradayChartSeriesLegendProps<T>): React.ReactElement<IntradayChartSeriesLegendProps<T>> => {
  const configs = useMemo(() => {
    const seriesWithoutTrend: typeof seriesConfig = [];
    let trendConfig: SeriesConfig<T> | undefined;

    seriesConfig?.forEach((config) => {
      if (config.nameKey !== 'trend') {
        seriesWithoutTrend.push(config);
      } else {
        trendConfig = config;
      }
    });

    return {
      seriesWithoutTrend,
      trendSeries: trendConfig,
    };
  }, [seriesConfig]);

  return (
    <div className="flex flex-col mbs-20 items-start" data-test-id="chart:legend-container">
      {configs.seriesWithoutTrend.map((config) => {
        const shouldRenderTrendSeries = seriesTrendLegendRelatedSeries === config.nameKey;
        const isToggleEnabled = countOfVisibleSeries > 1 || !seriesDataVisibility[config.nameKey as string];

        const handleToggleSeriesDataVisibility = () =>
          isToggleEnabled ? toggleSeriesDataVisibility(config.nameKey as string) : null;

        const commonProps = {
          config,
          isDataVisible: seriesDataVisibility[config.nameKey as string],
          isDataPresent: seriesLegendEnabled[config.nameKey as string],
          isEnabled: isToggleEnabled,
          toggleSeriesDataVisibility: handleToggleSeriesDataVisibility,
        };

        if (shouldRenderTrendSeries && configs.trendSeries) {
          return (
            <div className="flex gap-40" key={config.nameKey as string}>
              <IntradayChartSeriesLegendItem {...commonProps} />

              <IntradayChartSeriesLegendItem {...commonProps} config={configs.trendSeries} />
            </div>
          );
        }

        return <IntradayChartSeriesLegendItem key={config.nameKey as string} {...commonProps} />;
      })}
      {/* considering legend could contain 2 or 3 rows, we need to add one extra row so that chart's tiles height is consistent */}
      {shouldRenderExtraRow ? <div className="flex-1 h-full mbe-15">&nbsp;</div> : null}
    </div>
  );
};
